'use client';

import { formatPrice } from '@insta-club/utilities';
import dynamic from 'next/dynamic';
const Chart = dynamic(() => import('react-apexcharts'), { ssr: false });

interface ServerDataPoint {
    date: string;
    data: number;
}

interface ServerChartData {
    followers: Record<string, ServerDataPoint>;
    avg_likes: Record<string, ServerDataPoint>;
    avg_comments: Record<string, ServerDataPoint>;
}

interface Props {
    data: ServerChartData;
}

const AvgChart = ({ data }: Props) => {
    // تبدیل دیتای سرور به فرمت مورد نیاز AreaChart
    const transformData = (dataObj: Record<string, ServerDataPoint>) => {
        return Object.values(dataObj).map((item) => ({
            date: item.date,
            likes: item.data
        }));
    };

    const chartConfigs = [
        {
            key: 'followers',
            name: 'Followers',
            label: 'فالوور',
            data: transformData(data.followers).reverse()
        },
        {
            key: 'avg_likes',
            name: 'Like .Avg',
            label: 'لایک',
            data: transformData(data.avg_likes).reverse()
        },
        {
            key: 'avg_comments',
            name: 'Comment .Avg',
            label: 'کامنت',
            data: transformData(data.avg_comments).reverse()
        }
    ];

    return (
        <div>
            <h4 className='mb-4 text-xl font-semibold text-gray-900'>نمودار تغییرات آمار</h4>
            <div className='space-y-3'>
                {chartConfigs.map((chart) => (
                    <div key={chart.key}>
                        <AreaChart data={chart.data} type='date' name={chart.name} label={chart.label} />
                    </div>
                ))}
            </div>
        </div>
    );
};

export default AvgChart;

interface ChartData {
    date?: string;
    postName?: string;
    likes: number;
}

interface AreaChartProps {
    data: ChartData[];
    type?: 'date' | 'post';
    name: string;
    label?: string;
}

function AreaChart({ data, type = 'date', name, label = 'لایک' }: AreaChartProps) {
    const categories = data.map((item) => (type === 'date' ? item.date : item.postName));

    const options = {
        chart: {
            type: 'area' as const,
            height: 200,
            toolbar: {
                show: false
            },
            zoom: {
                enabled: false
            },
            fontFamily: 'inherit',
            offsetX: 0,
            offsetY: 0
        },
        dataLabels: {
            enabled: false
        },
        stroke: {
            curve: 'smooth' as const,
            width: 3,
            colors: ['#4d4da7']
        },
        fill: {
            type: 'gradient',
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.6,
                opacityTo: 0.05,
                stops: [0, 90, 100]
            },
            colors: ['#4d4da7']
        },
        xaxis: {
            categories: categories,
            labels: {
                style: {
                    colors: '#6b7280',
                    fontSize: '10px',
                    fontWeight: 500
                },
                offsetY: 2
            },
            axisBorder: {
                show: false
            },
            axisTicks: {
                show: false
            },
            tooltip: {
                enabled: false
            }
        },
        yaxis: {
            title: {
                text: name,
                style: {
                    color: '#6b7280',
                    fontSize: '12px',
                    fontWeight: 600
                }
            },
            labels: {
                
                style: {
                    colors: '#6b7280',
                    fontSize: '12px',
                    fontWeight: 500
                },
                formatter: function (value: number) {
                    return formatPrice(Math.round(value).toString());
                }
            }
        },
        grid: {
            borderColor: '#e5e7eb',
            strokeDashArray: 4,
            xaxis: { lines: { show: false } },
            yaxis: { lines: { show: true } },
            padding: { top: 0, right: 0, bottom: 0, left: 15 }
        },
        tooltip: {
            enabled: true,
            theme: 'light',
            style: {
                fontSize: '12px',
                fontFamily: 'inherit'
            },
            x: {
                show: false
            },
            y: {
                formatter: function (value: number) {
                    return `${value.toLocaleString()} ${label}`;
                },
                title: {
                    formatter: () => ''
                }
            }
        },
        colors: ['#4d4da7'],
        markers: {
            size: 0,
            colors: ['#4d4da7'],
            strokeColors: '#fff',
            strokeWidth: 2,
            hover: {
                size: 6
            }
        }
    };

    const series = [
        {
            name: label,
            data: data.map((item) => item.likes)
        }
    ];

    return (
        <div className='w-full'>
            <Chart options={options} series={series} type='area' height={200} />
        </div>
    );
}

